import type { UserManagementApi } from '@liks/backend-svc-api/esm/apis/UserManagementApi';
import type { KeycloakRoleData } from '@liks/backend-svc-api/esm/models/index';
import {
  KEYCLOAK_CLIENT_ROLES,
  type KeycloakClientRole,
} from '../auth/clientRoles';
import type { Locale } from '../i18n/resources';
import type { CurrentUserProfile } from '../types/user';
import { calendarValidityToIsoRange } from '../utils/dateTimeUtils';
import { hasUserId } from './userServiceGuards';
import {
  buildDirectoryUserUpdatePayload,
  toDirectoryEnabledUserPayload,
} from './userServicePayloads';
import {
  sendPasswordSetupEmailWithRetry,
  setUserRolesWithRetry,
  waitUntilUserReadable,
  waitUntilUserReadableByUsername,
} from './userServiceProvisioning';
import type {
  CreateDirectoryUserInput,
  CreateDirectoryUserResult,
  DirectoryUserWithId,
  SetDirectoryUserEnabledInput,
  UpdateDirectoryUserInput,
} from './userServiceTypes';

const MANAGED_CLIENT_ROLES = new Set<string>(KEYCLOAK_CLIENT_ROLES);

export async function getCurrentUser(
  userApi: UserManagementApi,
): Promise<CurrentUserProfile> {
  return userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceReadCurrentUser();
}

export async function updateCurrentUserLanguage(
  userApi: UserManagementApi,
  locale: Locale,
): Promise<void> {
  await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceUpdateCurrentUserLanguage(
    { locale },
  );
}

export async function getUser(
  userApi: UserManagementApi,
  userId: string,
): Promise<CurrentUserProfile> {
  return userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceReadUser({
    userId,
  });
}

export async function listUsers(
  userApi: UserManagementApi,
): Promise<DirectoryUserWithId[]> {
  const pageSize = 100;
  const users: DirectoryUserWithId[] = [];
  let offset = 0;

  for (;;) {
    const page =
      await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceListUsers(
        { offset, limit: pageSize },
      );

    users.push(...page.filter(hasUserId));

    if (page.length < pageSize) {
      break;
    }

    offset += pageSize;
  }

  return users;
}

export async function createUser(
  userApi: UserManagementApi,
  input: CreateDirectoryUserInput,
): Promise<CreateDirectoryUserResult> {
  const username = input.username.trim();
  const validity = calendarValidityToIsoRange(input);
  const created =
    await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceCreateUser({
      keycloakUserData: {
        username,
        email: input.email.trim(),
        firstName: input.firstName.trim(),
        lastName: input.lastName.trim(),
        ...validity,
        enabled: true,
      },
    });

  const createdId =
    created &&
    typeof created === 'object' &&
    'id' in created &&
    typeof (created as { id?: unknown }).id === 'string'
      ? (created as { id: string }).id.trim()
      : '';

  let userId = createdId;
  if (userId !== '') {
    await waitUntilUserReadable((id) => getUser(userApi, id), userId);
  } else {
    userId = (
      await waitUntilUserReadableByUsername(() => listUsers(userApi), username)
    ).id;
  }

  if (input.sendInviteEmail) {
    await sendPasswordSetupEmailWithRetry(userApi, userId);
  }

  try {
    await setUserRolesWithRetry(
      (id, roles) => setUserRoles(userApi, id, roles),
      userId,
      [input.role],
    );
    return { userId, username, roleAssigned: true };
  } catch {
    return { userId, username, roleAssigned: false };
  }
}

export async function updateUser(
  userApi: UserManagementApi,
  input: UpdateDirectoryUserInput,
): Promise<void> {
  await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceUpdateUser({
    userId: input.userId,
    keycloakUserData: buildDirectoryUserUpdatePayload(input),
  });
}

export async function getUserRoles(
  userApi: UserManagementApi,
  userId: string,
): Promise<string[]> {
  const roles = await getUserRoleData(userApi, userId);

  return roles
    .map((role) => role.name?.trim())
    .filter((name): name is string => Boolean(name));
}

export async function setUserEnabled(
  userApi: UserManagementApi,
  input: SetDirectoryUserEnabledInput,
): Promise<void> {
  await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceUpdateUser({
    userId: input.userId,
    keycloakUserData: toDirectoryEnabledUserPayload(
      input.profile,
      input.enabled,
    ),
  });
}

export async function deleteUser(
  userApi: UserManagementApi,
  userId: string,
): Promise<void> {
  await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceDeleteUser({
    userId,
  });
}

export async function resetUserPassword(
  userApi: UserManagementApi,
  userId: string,
  password: string,
  temporary = true,
): Promise<void> {
  await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceResetUserPassword(
    {
      userId,
      credentialsData: {
        type: 'password',
        value: password,
        temporary,
      },
    },
  );
}

export async function updateCurrentUserPassword(
  userApi: UserManagementApi,
  currentPassword: string,
  newPassword: string,
): Promise<void> {
  await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceUpdateCurrentUserPassword(
    {
      currentUserPassword: {
        currentPassword,
        newPassword,
      },
    },
  );
}

export async function downloadUserPhoto(
  userApi: UserManagementApi,
  userId: string,
): Promise<Blob | null | undefined> {
  return userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceDownloadUserPhoto(
    { userId },
  );
}

export async function uploadUserPhoto(
  userApi: UserManagementApi,
  userId: string,
  file: File,
): Promise<void> {
  await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceUploadUserPhoto(
    {
      userId,
      data: file,
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
    },
  );
}

export async function deleteUserPhoto(
  userApi: UserManagementApi,
  userId: string,
): Promise<void> {
  await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceDeleteUserPhoto(
    { userId },
  );
}

export async function setUserRoles(
  userApi: UserManagementApi,
  userId: string,
  roleNames: readonly KeycloakClientRole[],
): Promise<void> {
  const desiredNames = new Set<string>(roleNames);
  const [currentRoles, clientRoles] = await Promise.all([
    getUserRoleData(userApi, userId),
    listClientRoles(userApi),
  ]);

  const toUnassign = currentRoles.filter((role) => {
    const name = role.name?.trim();
    return Boolean(
      name && MANAGED_CLIENT_ROLES.has(name) && !desiredNames.has(name),
    );
  });

  const toAssign = clientRoles.filter((role) => {
    const name = role.name?.trim();
    return Boolean(name && desiredNames.has(name));
  });

  if (toUnassign.length > 0) {
    await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceUnassignUserRoles(
      {
        userId,
        keycloakRoleData: toUnassign,
      },
    );
  }

  if (toAssign.length > 0) {
    await userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceAssignUserRoles(
      {
        userId,
        keycloakRoleData: toAssign,
      },
    );
  }
}

async function getUserRoleData(
  userApi: UserManagementApi,
  userId: string,
): Promise<KeycloakRoleData[]> {
  return userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceReadUserRoles(
    { userId },
  );
}

async function listClientRoles(
  userApi: UserManagementApi,
): Promise<KeycloakRoleData[]> {
  return userApi.deLiksCommonUsermanagementBoundaryUserMgmtResourceListClientRoles();
}
